import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import ObjectEdit from "@components/objects/ObjectEdit";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import { ObjectRelationshipEdit } from "@components/objects/ObjectRelationshipEdit";
import { countries } from "@utils/selectOptions";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import SearchSelect from "@components/SearchSelect";
import { CheckPermission } from "@components/CheckPermission";
import { useDeleteBusiness, useGetBusiness, useUpdateBusiness } from "@api/businessService";
import { ContextualHelp } from "@components/ContextualHelp";
import { getBudget, useDeleteWorkPlan, useGetWorkPlan, useUpdateWorkPlan } from "@api/workplanService";
import { formatCurrency } from "@utils/numbers";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { useAuth } from "@providers/AuthProvider";


const budgetColumn = (label, type) => {
    return {
        label: label,
        type: 'info',
        content: (object) => {
            return (<>
                <FauxTable>
                    <FauxTableRow title={'Est.'}>
                        <Typography fontSize={'sm'}>{formatCurrency(getBudget(object?.tasks, `estimated_${type}`))}</Typography>
                    </FauxTableRow>
                    <FauxTableRow title={'Actual.'}>
                        <Typography fontSize={'sm'}>{formatCurrency(getBudget(object?.tasks, `actual_${type}`))}</Typography>
                    </FauxTableRow>
                </FauxTable>
            </>)
        },
    };
}

const fields = [
    {
        name: 'name',
        type: 'text',
        placeholder: 'e.g. 2024 Clean Up Day',
        showRequired: true,
        config: {
            required: true,
        }
    },
    {name: 'workplan_date', label: 'Date', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {
        name: 'event_chair_id',
        type: 'relationship',
        endpoint: 'contacts',
        defaultProp: 'event_chair',
    },
    budgetColumn('Income', 'income'),
    budgetColumn('Expense', 'expense'),
    {name: 'notes', type: 'textarea', placeholder: 'Notes...'},
];

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        <Avatar size="lg" sx={{'--Avatar-size': '5rem'}} src={data?.profile_image?.url}>{avatarInitials}</Avatar>
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>
            {data.title && <Typography fontSize={'sm'}>{data.title}</Typography>}
            {data.company_name && data.name !== data.company_name && <Typography fontSize={'sm'}>{data.company_name}</Typography>}
        </Box>
    </Stack>
)
};

export default function WorkPlanEdit(props) {
    const {user, can} = useAuth();

    const canEdit = ({data}) => {
        if(can('edit work plans')) {
            return true;
        }

        if(can('edit own work plans')) {
            console.log(user);
            const userIsConnected = data.contacts.find(contact => contact.id === user?.contact?.id);
            return !!userIsConnected;
            // return true;
        }

        return false;
    };

    const beforeSave = (object) => {
        const tasks = (object?.tasks ?? []).map(task => {
            if(task.due_date == '0') {
                task.due_date = null;
            }

            // Convert empty strings to 0
            const numericFields = ['estimated_budget', 'actual_budget', 'time_taken'];
            
            numericFields.forEach(field => {
                if (task.hasOwnProperty(field)) {
                    const value = task[field];
                    
                    // Convert empty/null/undefined to 0
                    if (value === '' || value === null || value === undefined) {
                        task[field] = 0;
                    }
                    // Convert string numbers to actual numbers
                    else if (typeof value === 'string') {
                        const parsed = parseFloat(value);
                        task[field] = isNaN(parsed) ? 0 : parsed;
                    }
                }
            });

            return task;
        });

        object.tasks = tasks;

        return object;
    };

    return (
        <ObjectEdit
            query={useGetWorkPlan}
            mutation={useUpdateWorkPlan}
            deleteMutation={useDeleteWorkPlan}
            type={'workplan'}
            permissionType={'work plans'}
            title={'Edit Work Plan'}
            subtitleKey={'name'}
            color={'workplan'}
            canEdit={canEdit}
            beforeSave={beforeSave}
            {...props}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfoEdit
                    title={'Work Plan Info'}
                    tint={'workplan'}
                    fields={fields}
                    // cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'contacts'} /*permissions={'edit work plans'}*/ />
                <ObjectRelationshipEdit type={'involvements'} /*permissions={'edit work plans'}*/ />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'notes'} column={'description'} title={'Description'} />

                <ObjectRelationshipEdit type={'transformation_strategies'} /*permissions={'edit work plans'}*/ />
                <ObjectRelationshipEdit type={'events'} />
                <ObjectRelationshipEdit type={'files'} /*permissions={'edit work plans'}*/ />
            </ObjectColumn>

            <ObjectColumn full>
                <ObjectRelationshipEdit type={'tasks'} />
            </ObjectColumn>

        </ObjectEdit>
    )
}
